<template>
  <v-dialog 
    :class="['cds-dialog', `${displayedShortTitle.toLowerCase().replace(/ /g, '-')}-cds-dialog`, !modal ? 'nonmodal' : '']" 
    v-model="showDialog" 
    :scrim="scrim"
    :persistent="persistent"
    :no-click-animation="!modal"
    >
    <!-- add the activator slot, but only use it if the appropriate value is given for activator -->
     <template v-slot:activator="$attrs">
      <slot name="activator" v-bind="$attrs">
        <v-btn
          v-if="button"
          :color="color"
          :title="displayedShortTitle"
          class="cds-dialog-button"
          @click="showDialog = true"
          @keyup.enter="showDialog = true"
          tabindex="0"
          >
          Open {{ displayedShortTitle }}
        </v-btn>
      </slot>
    </template>
     
    <v-card
      ref="card"
      class="cds-dialog-card"
    >
        <v-toolbar
          density="compact"
          :color="titleColor"
        >
          <v-toolbar-title :text="title"></v-toolbar-title>
          <v-spacer></v-spacer>
          <v-btn
            class="cds-dialog-close-icon"
            icon="mdi-close"
            @click="close"
            @keyup:enter="close"
          >
          </v-btn>
        </v-toolbar>
      
      <v-card-text>
        
        <slot>
          Add content to the default slot
        </slot>
        
      </v-card-text>
    </v-card>
  </v-dialog>

</template>

<script setup lang="ts">
import { computed, ref, onMounted, nextTick, watch, useAttrs, useTemplateRef } from "vue";
import { VCard } from "vuetify/components";
import { UseDraggableDialogOptions, useDraggableDialog } from "../composables/useDraggableDialog";

interface CDSDialogProps {
  title: string;
  modelValue?: boolean;
  color?: string;
  titleColor?: string;
  shortTitle?: string;
  draggable?: boolean;
  button?: boolean;
  scrim?: boolean;
  persistent?: boolean;
  modal?: boolean;
}

const props = withDefaults(defineProps<CDSDialogProps>(), {
  modelValue: false,
  color: "red",
  shortTitle: "",
  draggable: false,
  button: false,
  titleColor: "primary",
  scrim: true,
  persistent: false,
  modal: true,
});

const emit = defineEmits<{
  (event: "update:modelValue", value: boolean): void;
}>();

const card = useTemplateRef<InstanceType<typeof VCard>>("card");
const showDialog = ref(props.modelValue);
const displayedShortTitle = computed(() => props.shortTitle || props.title);
const cardRoot = ref<HTMLElement | null>(null);

const attrs = useAttrs();

if (props.draggable) {
  const options: UseDraggableDialogOptions = {
    root: cardRoot,
    dialogSelector: ".cds-dialog-card",
  };
  if (attrs["drag-predicate"]) {
    options.dragPredicate = attrs["drag-predicate"] as (element: HTMLElement) => boolean;
  }
  useDraggableDialog(options);
}

function updateRoot() {
  nextTick(() => {
    if (card.value) {
      cardRoot.value = card.value.$el;
    }
  });
}

function close(event: PointerEvent) {
  showDialog.value = false;
  event.stopPropagation();
}

onMounted(() => {
  if (props.draggable && props.modelValue) {
    updateRoot();
  }
});

watch(showDialog, value => {
  emit("update:modelValue", value);
});

watch(() => props.modelValue, value => {
  showDialog.value = value;
  if (value && props.draggable) {
    updateRoot();
  }
});

</script>

<style>

.cds-dialog {
  display: flex;
  width: calc(100%);
}
.cds-dialog-card {
  align-self: center;
  max-width: 80%;
}

.cds-dialog-close-icon {
  cursor: pointer;
}

.v-dialog.cds-dialog > .v-overlay__content {
  align-self: center;
  margin: unset;
}

.cds-dialog .v-card-text {
  height: fit-content;
  max-height: 60vh;
}

.cds-dialog.nonmodal {

  .v-overlay__content {
    visibility: hidden;

    .v-card {
      visibility: visible;
    }
  }

}
</style>
